import ButtonMedium4 from "components/elements/ButtonMedium4"
import { Box, Drawer, IconButton, useMediaQuery } from "@mui/material"
import ContentNormal from "components/elements/ContentNormal"
import SectionWrapper from "components/layouts/SectionWrapper"
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import { Close, Edit, Menu } from "@mui/icons-material"
import { setupUserData } from "utils/setup"
import { callAPI } from "utils/API"
import { useContext, useEffect, useRef, useState } from "react"
import { AppContext } from "contexts/AppContext"
import Image from "components/elements/Image"
import RoundIconButton1 from "components/elements/RoundIconButton1"
import Headline5 from "components/elements/Headline5"
import Headline3 from "components/elements/Headline3"
import { translate } from "utils/Translate"
import Headline4 from "components/elements/Headline4"
import ContentLarge from "components/elements/ContentLarge"


const Profile = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const isMobile = useMediaQuery('(max-width:1100px)')
    const contextState = useContext(AppContext)
    const {accessibleShops, styleGuide, userData, brandData} = contextState
    const inputFile = useRef(null);
    const [profileImage, setProfileImage] = useState(userData.attachments && userData.attachments.length
      ? userData.attachments[0].imageOptimizedSourceSmall
      : "/profile.png"
    )
    const [showBurgerMenu, setShowBurgerMenu] = useState(false)
    const [activePath, setActivePath] = useState(window.location.pathname)

    const boxBgColor = brandData?.features?.profileBannerBgColor || styleGuide.color3
    const defaultShopID = accessibleShops && accessibleShops.length > 0
        ? accessibleShops[0].ShopID
        : null

    const openFileInput = () => {
        // `current` points to the mounted file input element
        inputFile?.current?.click();
    };

    const updateProfileImage = (event) => {
        const file = event.target && event.target.files && event.target.files.length
            ? event.target.files[0]
            : null;
        if (!file) {
            return false;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
            let _file = {
                File: file,
                base64String: reader.result,
                contentType: file.type,
                fileExtension: file.type.split("/")[1],
                originalName: file.originalName,
                info: {
                    width: null,
                    height: null,
                    size: file.size,
                    format: file.type.split("/")[1],
                },
            };
            setProfileImage(reader.result);
            let res = await callAPI("/image/upload", "POST", {
                body: { image: _file },
            });
            if (res.insertId) {
                let response = await callAPI("/user/updateUserProfileImage", "POST", {
                    body: { ImageID: res.insertId },
                });
                if (response) {
                    setupUserData(contextState, () => { });
                }
            }
        };
        reader.onerror = function () { };
        return false;
    };

    const pathHeadline = () => {
        switch(activePath){
            case 'settings':
                return translate('_PROFILE_MENU_SETTINGS_', 'Indstillinger')
            case 'messages':
                return translate('_PROFILE_PAGE_INBOX_', 'Din indbakke')
            case 'orders':
                return translate('_PROFILE_PAGE_YOUR_ORDERS_', 'Dine ordrer')
            case 'points':
                return translate('_PROFILE_PAGE_POINTS_', 'Din pointoversigt')
            case 'wishlist':
                return translate('_PROFILE_PAGE_YOUR_WISHLIST_', 'Din ønskeliste')
            case 'tax':
                return translate('_PROFILE_PAGE_TAX_', 'Beskatning')
            case 'customer-cards':
                return translate('_PROFILE_PAGE_CUSTOMER_CARDS_', 'Kundekort')
            case 'coupons':
                return translate('_PROFILE_PAGE_COUPONS_', 'Vouchers')
        }

        return ''
    }

    useEffect(() => {
        const pathElements = location.pathname.split('/')
        const subPath = pathElements.length > 2 ? pathElements[2] : ''
        setActivePath(subPath)
    }, [location.pathname])

    const defualtPath = brandData?.features?.hideProfileStatsAndShowOnlySettings
        ? 'settings'
        : !brandData?.features?.disableProfilePointsPage
            ? 'points'
            : brandData?.features?.enableWalletActivation && !brandData?.features?.disableProfileTaxPage
                ? 'tax'
                : 'orders'

    if(activePath === '') {
        return navigate(defualtPath)
    }

    return (
        <>
            {/* Header with profile image and user info */}
            <SectionWrapper backgroundColor={boxBgColor}>
                <Box display={'flex'} paddingY={5} gap={4} flexWrap={'wrap'} justifyContent={'space-between'}>
                    <Box display={'flex'} gap={isMobile ? 2 : 6}>
                        <Box>
                            <Image
                                style={{
                                    backgroundColor: styleGuide.productInfoBackground,
                                    position: 'relative',
                                    height: isMobile ? 140 : 200,
                                    width: isMobile ? 140 : 200,
                                    borderRadius: 14,
                                }}
                                src={`url(${profileImage}`}
                            >
                                <RoundIconButton1 onClick={openFileInput} icon={<Edit fill="white" fontSize="small" />} 
                                    style={{
                                        position: "absolute",
                                        height: 32,
                                        width: 32,
                                        right: -8,
                                        top: -8,
                                    }}
                                />
                            </Image>
                            <input type="file" id="file" accept="image/*" ref={inputFile} style={{ display: "none" }} onChange={updateProfileImage} />
                        </Box>
                        
                        <Box display={'flex'} flexDirection={'column'} gap={1}>
                            {isMobile
                                ? <Headline5>{userData.userName}</Headline5>
                                : <Headline3>{userData.userName}</Headline3>
                            }

                            {!!userData.userTitle && (isMobile
                                ? <ContentNormal fallbackText={userData.userTitle} />
                                : <ContentLarge fallbackText={userData.userTitle} />
                            )}

                            {brandData?.features?.displayDepartmentAsProfileSubheading
                                ? !!userData.userDepartment && (isMobile
                                    ? <ContentNormal fallbackText={userData.userDepartment} />
                                    : <ContentLarge fallbackText={userData.userDepartment} />
                                )
                                : !!userData.userCompany && (isMobile
                                    ? <ContentNormal fallbackText={userData.userCompany} />
                                    : <ContentLarge fallbackText={userData.userCompany} />
                                )
                            }
                        </Box>
                    </Box>

                    <Box display={'flex'} gap={1}>
                        <ButtonMedium4
                            style={{ maxWidth: 116 }}
                            showIcon={false}
                            onClick={() => navigate('/contact', {state:{scrollToContact:true}})}
                            translationKey="_CONTACT_US_BUTTON_TEXT_"
                            fallbackText='Kontakt os'
                        />

                        {defaultShopID &&
                            <ButtonMedium4
                                style={{ maxWidth: 100 }}
                                showIcon={false}
                                onClick={() => navigate(`/shop/${defaultShopID}`, { state: { scrollToTop: true } })}
                                fallbackText='Shop'
                            />
                        }
                    </Box>
                </Box>
            </SectionWrapper>

            {/* Main content */}
            <SectionWrapper>
                <Box display={'flex'} paddingY={5} gap={4}>
                    {/* Left menu */}
                    {!isMobile &&
                        <ProfileMenu brandData={brandData} activePath={activePath} />
                    }

                    <Box display={'flex'} flexDirection={'column'} gap={4} flex={1} width={'100%'}>
                        <Box display={'flex'} gap={2} alignItems={'center'}>

                            {/* Mobile burger menu */}
                            {isMobile ?
                                <>
                                    <IconButton onClick={() => setShowBurgerMenu(true)}>
                                        <Menu/>
                                    </IconButton>
                                    <Headline4 fallbackText={pathHeadline()} />
                                </>
                                :
                                <Headline3 fallbackText={pathHeadline()} />
                            }
                        </Box>

                        <Outlet />
                    </Box>
                </Box>
            </SectionWrapper>

            {isMobile &&
                <Drawer open={showBurgerMenu} onClose={() => setShowBurgerMenu(false)} sx={{ zIndex: (theme) => theme.zIndex.appBar - 1 }} >
                    <Box sx={{display: 'flex', width: 1200, padding: 4, flexDirection: 'column', paddingTop: 17, gap: 4}}>
                        <Box maxWidth={'100vw'} paddingRight={6} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                            <Headline4 
                                translationKey='_PROFILE_MENU_HEADLINE_'
                                fallbackText='Profiloversigt'
                            />
                            <IconButton onClick={() => setShowBurgerMenu(false)}>
                                <Close />
                            </IconButton>
                        </Box>
                        <ProfileMenu brandData={brandData} activePath={activePath} onClick={() => setShowBurgerMenu(false)} />
                    </Box>
                </Drawer>
            }
        </>
    )
}

const ProfileMenu = (props) => {
    return(
        <Box display={'flex'} flexDirection={'column'} width={220} gap={3}>
            {!props.brandData?.features?.hideProfileStatsAndShowOnlySettings &&
                <>
                    {!props.brandData?.features?.disableProfilePointsPage &&
                        <ProfileNavButton to={'points'} text={translate('_PROFILE_MENU_POINTS_', 'Point')} activePath={props.activePath} onClick={props.onClick} />
                    }

                    {!!props.brandData?.features?.enableWalletActivation && !props.brandData?.features?.disableProfileTaxPage &&
                        <ProfileNavButton to={'tax'} text={translate('_PROFILE_MENU_TAX_', 'Beskatning')} activePath={props.activePath} onClick={props.onClick} />
                    }

                    <ProfileNavButton to={'orders'} text={translate('_PROFILE_MENU_ORDERS_', 'Ordrer')} activePath={props.activePath} onClick={props.onClick} />
                    
                    {!!props.brandData?.features?.enableProfileCustomerCardsPage &&
                        <ProfileNavButton to={'customer-cards'} text={translate('_PROFILE_MENU_CUSTOMER_CARDS_', 'Kundekort')} activePath={props.activePath} onClick={props.onClick} />
                    }

                    {!!props.brandData?.features?.enableProfileCouponsPage &&
                        <ProfileNavButton to={'coupons'} text={translate('_PROFILE_MENU_COUPONS_', 'Vouchers')} activePath={props.activePath} onClick={props.onClick} />
                    }

                    {!props.brandData?.features?.disableWishlist &&
                        <ProfileNavButton to={'wishlist'} text={translate('_PROFILE_MENU_WISHLIST_', 'Ønskeliste')} activePath={props.activePath} onClick={props.onClick} />
                    }

                    {!!props.brandData?.features?.Inbox &&
                        <ProfileNavButton to={'messages'} text={translate('_PROFILE_MENU_INBOX_', 'Beskeder')} activePath={props.activePath} onClick={props.onClick} />
                    }
                </>
            }
            {(!props.brandData?.features?.hideProfileMenuSetting && !props.brandData?.features?.disableProfileStatistic) &&
                <ProfileNavButton to={'settings'} text={translate('_PROFILE_MENU_SETTINGS_', 'Indstillinger')} activePath={props.activePath} onClick={props.onClick} />
            }
        </Box>
    )
}

const ProfileNavButton = (props) => {
    const [active, setActive] = useState(false)
    useEffect(() => {
        setActive(props.activePath === props.to)
    }, [props.activePath])
    return (
        <Link
            to={props.to}
            onClick={props.onClick}
            style={{
                color: '#000000',
                textDecoration: 'none',
                fontWeight: active ? 700 : 400
            }}
        >
            {props.text}
        </Link>
    )
}

export default Profile